import { useSyncExternalStore } from 'react';
import {
  allowActiveRunPersistenceWrites,
  blockActiveRunPersistenceWrites,
  hasActiveRunPersistenceWrites,
  resetActiveRunPersistenceWrites,
  waitForActiveRunPersistenceWrites,
} from './persistenceWriteBarrier';

export type ActiveRunIdentity = {
  userId: string;
  isAnonymous: boolean;
};

export type ActiveRunIdentityStatus = 'ready' | 'resolving' | 'claiming' | 'blocked';

export type ActiveRunIdentityState = {
  status: ActiveRunIdentityStatus;
  identity: ActiveRunIdentity | null;
  generation: number;
};

export type EmbeddedGameRunIdentityContext = {
  anonymousId: string;
  generation: number;
};

type TransitionOptions = {
  /** Claims the previous anonymous identity before an auth rebind. */
  claim?: (anonymousId: string) => Promise<AnonymousClaimOutcome>;
  /** Ends the mounted run before switching between principals. */
  resetRun?: () => void;
};

export type AnonymousClaimOutcome = 'claimed' | 'terminal' | 'failed';

const initialState: ActiveRunIdentityState = {
  status: 'resolving',
  identity: null,
  generation: 0,
};

let state = initialState;
let transitionToken = 0;
let authEventRevision = 0;
const listeners = new Set<() => void>();

const emit = () => {
  for (const listener of listeners) listener();
};

const setState = (next: ActiveRunIdentityState) => {
  state = next;
  emit();
};

const sameIdentity = (left: ActiveRunIdentity | null, right: ActiveRunIdentity | null) =>
  left?.userId === right?.userId && left?.isAnonymous === right?.isAnonymous;

export const getActiveRunIdentityState = (): ActiveRunIdentityState => state;

/** Captures the exact writable guest principal owned by the mounted Game Run. */
export function captureEmbeddedGameRunIdentityContext(): EmbeddedGameRunIdentityContext {
  const activeIdentity = getActiveRunIdentityState();
  if (activeIdentity.status !== 'ready' || !activeIdentity.identity?.isAnonymous) {
    throw new Error('The game identity is not ready for sign-in');
  }

  return {
    anonymousId: activeIdentity.identity.userId,
    generation: activeIdentity.generation,
  };
}

/** Prevents an OAuth result from being installed into a replacement Game Run. */
export function isEmbeddedGameRunIdentityContextCurrent(
  context: EmbeddedGameRunIdentityContext
): boolean {
  const activeIdentity = getActiveRunIdentityState();
  return (
    activeIdentity.status === 'ready' &&
    activeIdentity.identity?.isAnonymous === true &&
    activeIdentity.identity.userId === context.anonymousId &&
    activeIdentity.generation === context.generation
  );
}

export function sameEmbeddedGameRunIdentityContext(
  left: EmbeddedGameRunIdentityContext | null,
  right: EmbeddedGameRunIdentityContext | null
): boolean {
  return (
    left !== null &&
    right !== null &&
    left.anonymousId === right.anonymousId &&
    left.generation === right.generation
  );
}

export const subscribeActiveRunIdentity = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * Waits until the mounted Game Run has completed its guest-to-account rebound.
 * A different authenticated subject fails closed instead of satisfying the wait.
 */
export function waitForAuthenticatedActiveRunIdentity(
  expectedUserId: string,
  sourceGeneration: number,
  timeoutMs = 5000
): Promise<boolean> {
  const inspect = (): boolean | null => {
    const current = getActiveRunIdentityState();
    if (current.status !== 'ready' || current.identity?.isAnonymous !== false) return null;
    if (current.identity.userId !== expectedUserId) return false;
    return current.generation > sourceGeneration ? true : null;
  };

  const immediate = inspect();
  if (immediate !== null) return Promise.resolve(immediate);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (outcome: boolean) => {
      if (settled) return;
      settled = true;
      globalThis.clearTimeout(timeoutId);
      unsubscribe();
      resolve(outcome);
    };
    const unsubscribe = subscribeActiveRunIdentity(() => {
      const outcome = inspect();
      if (outcome !== null) finish(outcome);
    });
    const timeoutId = globalThis.setTimeout(() => finish(false), timeoutMs);
  });
}

/**
 * React-facing view of the active run's server persistence identity.
 * A claiming or failed claim deliberately returns no writable identity.
 */
export const useActiveRunIdentity = (): ActiveRunIdentityState =>
  useSyncExternalStore(
    subscribeActiveRunIdentity,
    getActiveRunIdentityState,
    getActiveRunIdentityState
  );

export const resetActiveRunIdentityState = () => {
  transitionToken += 1;
  authEventRevision = 0;
  resetActiveRunPersistenceWrites();
  setState(initialState);
};

/**
 * Called synchronously from Supabase's auth event callback, before React is
 * notified. This closes the event-to-render window in which a stale machine
 * could write under the previous JWT.
 */
export const notifyActiveRunAuthEvent = (): number => {
  blockActiveRunPersistenceWrites();
  authEventRevision += 1;
  transitionToken += 1;
  setState({
    status: 'resolving',
    identity: state.identity,
    generation: state.generation + 1,
  });
  return authEventRevision;
};

export const getActiveRunAuthEventRevision = (): number => authEventRevision;

/** Starts a canonical JWT identity lookup; writes stay blocked until it ends. */
export const beginActiveRunIdentityResolution = (): number => {
  blockActiveRunPersistenceWrites();
  const token = ++transitionToken;
  setState({
    status: 'resolving',
    identity: state.identity,
    generation: state.generation + 1,
  });
  return token;
};

export const isActiveRunIdentityResolutionCurrent = (token: number): boolean =>
  token === transitionToken;

/**
 * Applies one auth identity transition. This is the only seam that decides
 * whether an in-memory run may continue, must end, or must wait for a claim.
 */
export async function transitionActiveRunIdentity(
  nextIdentity: ActiveRunIdentity | null,
  options: TransitionOptions = {}
): Promise<boolean> {
  const token = ++transitionToken;
  const previousState = state;
  const previousIdentity = previousState.identity;

  if (sameIdentity(previousIdentity, nextIdentity) && previousState.status === 'ready') {
    return true;
  }

  blockActiveRunPersistenceWrites();

  const isAnonymousToAuthenticated =
    previousIdentity?.isAnonymous === true && nextIdentity?.isAnonymous === false;
  const isPrincipalReplacement =
    previousIdentity !== null &&
    !sameIdentity(previousIdentity, nextIdentity) &&
    !isAnonymousToAuthenticated;

  if (isAnonymousToAuthenticated && nextIdentity) {
    if (!options.claim) {
      setState({
        status: 'blocked',
        identity: previousIdentity,
        generation: previousState.generation + 1,
      });
      return false;
    }

    setState({
      status: 'claiming',
      identity: previousIdentity,
      generation: previousState.generation + 1,
    });

    if (hasActiveRunPersistenceWrites()) {
      await waitForActiveRunPersistenceWrites();
      if (token !== transitionToken) return false;
    }

    let claimOutcome: AnonymousClaimOutcome;
    try {
      claimOutcome = await options.claim(previousIdentity.userId);
    } catch {
      claimOutcome = 'failed';
    }

    if (token !== transitionToken) return false;

    if (claimOutcome === 'terminal') {
      setState({
        status: 'blocked',
        identity: previousIdentity,
        generation: state.generation + 1,
      });
      options.resetRun?.();
      await Promise.resolve();
      if (token !== transitionToken) return false;
      setState({
        status: 'ready',
        identity: nextIdentity,
        generation: state.generation + 1,
      });
      allowActiveRunPersistenceWrites();
      return true;
    }

    if (claimOutcome !== 'claimed') {
      setState({
        status: 'blocked',
        identity: previousIdentity,
        generation: state.generation + 1,
      });
      return false;
    }

    setState({
      status: 'ready',
      identity: nextIdentity,
      generation: state.generation + 1,
    });
    allowActiveRunPersistenceWrites();
    return true;
  }

  if (isPrincipalReplacement) {
    setState({
      status: 'blocked',
      identity: previousIdentity,
      generation: previousState.generation + 1,
    });
    if (hasActiveRunPersistenceWrites()) {
      await waitForActiveRunPersistenceWrites();
      if (token !== transitionToken) return false;
    }
    options.resetRun?.();

    // Let React flush the run teardown before exposing the replacement
    // identity. This closes the post-commit window where a stale machine could
    // receive one more answer event under the new JWT.
    await Promise.resolve();
    if (token !== transitionToken) return false;
  }

  if (!nextIdentity) {
    setState({
      status: 'blocked',
      identity: null,
      generation: state.generation + (isPrincipalReplacement ? 1 : 0),
    });
    return false;
  }

  setState({
    status: 'ready',
    identity: nextIdentity,
    generation: state.generation + (isPrincipalReplacement ? 1 : 0),
  });
  allowActiveRunPersistenceWrites();
  return true;
}
